import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import { ProductHit } from "@/components/atoms/search/searchbar";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { Badge } from "@/components/ui/badge";
import { Locales } from "@/lib/i18n";
import { __, _x, getPermalink } from "@/lib/i18n/functions";
import { cn, formatPrice } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { normalizeConditioning, resolveProductConditioning } from "@/lib/product-units";
import StorefrontRouteGuard from "@/components/molecules/storefront-route-guard";

const SearchResultCard = ({
  product,
  locale,
  isInNavbar,
}: {
  product: ProductHit;
  locale: Locales;
  isInNavbar?: boolean;
}) => {
  const title = product?.title ?? "Untitled Product";
  const slug = product?.slug ?? "#";
  const images = Array.isArray(product?.images) ? product.images : [];
  const featuredImage = product?.featuredImage;
  const productImage = featuredImage || images[0];
  const categories = product?.categories || {};
  const price = typeof product?.price === "number" ? product.price : null;
  const conditioning = resolveProductConditioning(product?.conditioning, product?.conditioning_bac);
  const physicalPieceCount = normalizeConditioning(conditioning);
  const isCollection = !!product?.isCollection;
  const isRange = product?.is_surface_based;
  const isPriceDynamic = product?.is_price_dynamic;
  const { product_type } = product;

  return (
    <StorefrontRouteGuard locale={locale} slug={slug}>
      <article className={cn("group/card relative w-full space-y-1", isInNavbar && "gap-3.5 max-md:flex")}>
        <Link
          href={`${getPermalink(locale, "product")}/${slug}`}
          className="focus-visible:ring-dark-primary/80 absolute inset-0 z-10 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-offset-2"
          aria-label={_x("View the product {{ productName }}", { productName: title }, locale)}
        >
          <span className="sr-only">{_x("View the product {{ productName }}", { productName: title })}</span>
        </Link>

        <figure
          className={cn(
            "bg-product-card relative flex h-36 w-full items-center justify-center gap-2 overflow-hidden rounded p-4",
            isInNavbar && "max-md:h-[5.5rem] max-md:w-28",
            product?.image_full_size_card && "p-0"
          )}
        >
          <div
            aria-label={_x("Product image of {{ productName }}", { productName: title }, locale)}
            className="relative flex h-full w-full items-center justify-center overflow-hidden"
          >
            {productImage ? (
              <Image
                src={sanitizeImageUrl(productImage)}
                fill
                className={cn(
                  "outline-offset-2 transition-transform duration-300 ease-in-out group-hover/card:scale-105",
                  product?.image_full_size_card ? "object-cover" : "object-contain mix-blend-multiply"
                )}
                alt={_x("Product image of {{ productName }}", { productName: title }, locale)}
              />
            ) : (
              <span className="text-xs opacity-50">{__("No Image", locale)}</span>
            )}
          </div>
        </figure>

        <div className="flex flex-col gap-1">
          <p className="text-[10px] text-gray-400 uppercase">
            {Array.isArray(categories?.lvl0)
              ? categories.lvl0.at(0)
              : typeof categories?.lvl0 === "string"
                ? categories.lvl0
                : ""}
          </p>
          <p className={cn("text-xs text-neutral-950 group-hover:underline", isInNavbar && "max-sm:w-4/5")}>{title}</p>
          {!isCollection && price !== null && !isRange && !isPriceDynamic && (
            <p className="text-sm font-semibold">
              {formatPrice(price, { locale: locale })}{" "}
              {physicalPieceCount > 1 && (
                <span className="text-primary-light text-sm">{`/ ${conditioning} ${__("piece", locale)}`}</span>
              )}
            </p>
          )}
          <div className="flex items-center justify-between">
            {(isPriceDynamic || isRange) && <PriceToastDropdown locale={locale} />}
            {product_type === "Sale Product" && <Badge variant="festi">{__("To sell", locale)}</Badge>}
          </div>
        </div>
      </article>
    </StorefrontRouteGuard>
  );
};

export default SearchResultCard;
